import React from 'react';
import { Trash2, ZoomIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ZOOM_LIMITS, zoomViewport, type ZoomSegment } from '../lib/videoZoom';
import { PanelHeader, PanelSectionTitle, TimingFields } from './EditorPanelParts';
import { formatPanelTime } from '../lib/editorTime';

interface VideoZoomPanelProps {
  zoom: ZoomSegment;
  /** Video width / height, for the focus picker's shape. */
  aspect: number;
  duration: number;
  onChange: (patch: Partial<ZoomSegment>) => void;
  onDelete: () => void;
}

/** Settings of the selected zoom segment: zoom level, focus point and timing. */
export function VideoZoomPanel({ zoom, aspect, duration, onChange, onDelete }: VideoZoomPanelProps) {
  const { t } = useTranslation();
  const pct = Math.round(zoom.scale * 100);
  const area = zoomViewport(zoom);

  const setFocus = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const share = (v: number) => Math.min(1, Math.max(0, v));
    onChange({ x: share((e.clientX - rect.left) / rect.width), y: share((e.clientY - rect.top) / rect.height) });
  };

  return (
    <section className="ve-props-panel-body" aria-label={t('video_editor_zoom.title', 'Zoom')}>
      <PanelHeader
        icon={<ZoomIn size={16} />}
        title={t('video_editor_zoom.title', 'Zoom')}
        subtitle={t('video_editor_panel.shown', 'Shown {{start}} – {{end}}', {
          start: formatPanelTime(zoom.start),
          end: formatPanelTime(zoom.end),
        })}
      />

      <PanelSectionTitle>{t('video_editor_zoom.level', 'Zoom level')}</PanelSectionTitle>
      <label className="ve-props-label">
        <span className="ve-frame-slider-head">
          {t('video_editor_zoom.level_hint', 'How far to zoom in.')}
          <span className="ve-props-size-value">{pct}%</span>
        </span>
        <input
          type="range"
          min={ZOOM_LIMITS.min * 100}
          max={ZOOM_LIMITS.max * 100}
          step={5}
          value={pct}
          onChange={(e) => onChange({ scale: Number(e.target.value) / 100 })}
        />
      </label>

      <PanelSectionTitle>{t('video_editor_zoom.focus', 'Focus')}</PanelSectionTitle>
      <div
        className="ve-zoom-focus"
        style={{ aspectRatio: String(aspect > 0 ? aspect : 16 / 9) }}
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); setFocus(e); }}
        onPointerMove={(e) => { if (e.buttons === 1) setFocus(e); }}
        role="presentation"
      >
        <div
          className="ve-zoom-focus-area"
          style={{
            left: `${area.left * 100}%`,
            top: `${area.top * 100}%`,
            width: `${area.width * 100}%`,
            height: `${area.height * 100}%`,
          }}
        />
      </div>
      <p className="ve-props-hint">
        {t('video_editor_zoom.focus_hint', 'Click or drag in the box to choose where to zoom. The blue area is what stays visible.')}
      </p>

      <PanelSectionTitle>{t('video_editor_panel.timing', 'Timing')}</PanelSectionTitle>
      <TimingFields
        start={zoom.start}
        end={zoom.end}
        duration={duration}
        onChange={(start, end) => onChange({ start, end })}
      />

      <div className="ve-props-actions">
        <button type="button" className="ve-props-btn-delete" onClick={onDelete}>
          <Trash2 size={14} aria-hidden />
          {t('video_editor_zoom.delete', 'Delete zoom')}
        </button>
      </div>
    </section>
  );
}
